import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ExclamationTriangleIcon, EyeIcon } from '@heroicons/react/24/outline';

// Mirrors the server's range. There is no format beyond a length: Indian account numbers
// run 9-18 digits with no check digit and no national standard.
export const MIN_ACCOUNT_DIGITS = 9;
export const MAX_ACCOUNT_DIGITS = 18;
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const HOLDER_REGEX = /^[A-Za-z][A-Za-z.' -]{1,99}$/;
const UNREAD_HINT = 'We could not read this. Please type it from your passbook, cheque or bank app.';

export const accountDigits = (value) => String(value || '').replace(/\D/g, '');
export const normalizeIfscInput = (value) => String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11);

export function isValidAccountNumber(value) {
  const digits = accountDigits(value);
  return digits.length >= MIN_ACCOUNT_DIGITS && digits.length <= MAX_ACCOUNT_DIGITS;
}

export const isValidIfsc = (value) => IFSC_REGEX.test(normalizeIfscInput(value));
export const isValidHolderName = (value) => HOLDER_REGEX.test(String(value || '').trim());

/** Every field the server will require, in the shape it checks them. */
export function isBankFormComplete({ account, ifsc, holderName, ifscLookup }) {
  return isValidAccountNumber(account) &&
    isValidIfsc(ifsc) &&
    ifscLookup?.valid !== false &&
    isValidHolderName(holderName);
}

/**
 * The bank details the candidate confirms: account number, IFSC (with the bank and branch
 * it belongs to), and the name on the account.
 *
 * Every field starts from what was read off the document and every field is editable,
 * because the read is often partial and the candidate is the only one who knows the
 * answer. The server stores their values next to what the document showed and sends any
 * difference to the recruiter — so editing is never a way around the check, and nothing
 * here needs to stop them from correcting a misread.
 */
export default function BankDetailsForm({
  idPrefix,
  token,
  value,
  onChange,
  onCommit,
  confirmed,
  onConfirmChange,
  readAccount,
  possiblyTruncated,
  ifscSuggested,
  warnings,
  hasAttemptedSubmit,
  disabled,
  onView,
}) {
  const { account = '', ifsc = '', holderName = '', ifscLookup = null } = value || {};
  const accountValid = isValidAccountNumber(account);
  const ifscValid = isValidIfsc(ifsc);
  const holderValid = isValidHolderName(holderName);
  // Ticking the box is the candidate's "save". If a field is still wrong, saying so right
  // then is the difference between fixing it and believing it went through.
  const showErrors = hasAttemptedSubmit || confirmed;
  const complete = isBankFormComplete(value || {});

  const read = accountDigits(readAccount);
  const typed = accountDigits(account);
  const accountDiffers = !!read && accountValid && typed !== read && !(possiblyTruncated && typed.includes(read));

  // Name the bank and branch for the IFSC as it is typed, so a wrong code is visible
  // before it is submitted. Token-gated server lookup against the RBI list.
  const [lookupState, setLookupState] = useState('idle');
  const normalizedIfsc = normalizeIfscInput(ifsc);
  useEffect(() => {
    if (!IFSC_REGEX.test(normalizedIfsc)) {
      setLookupState('idle');
      if (ifscLookup) onChange({ ifscLookup: null });
      return undefined;
    }
    if (ifscLookup?.ifsc === normalizedIfsc) return undefined;
    let cancelled = false;
    setLookupState('loading');
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`/api/documents/upload/${token}/ifsc/${normalizedIfsc}`);
        if (!cancelled) {
          onChange({ ifscLookup: res.data });
          setLookupState('done');
        }
      } catch (_err) {
        // A lookup failure never blocks: the server checks the code again on upload.
        if (!cancelled) setLookupState('failed');
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedIfsc, token]);

  const inputClass = (invalid, ok) => `block w-full rounded-xl border ${invalid
    ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
    : ok
      ? 'border-emerald-400 focus:border-emerald-500 focus:ring-emerald-500/20'
      : 'border-gray-200 focus:border-electric-blue focus:ring-electric-blue/20'
  } px-3.5 py-3 sm:py-2.5 text-base sm:text-sm focus:outline-none focus:ring-2 bg-white shadow-2xs transition ${disabled ? 'opacity-50 cursor-not-allowed text-gray-500' : 'text-gray-900'}`;

  const commit = () => {
    if (confirmed) onCommit();
  };

  return (
    <div className="mt-3 sm:mt-4 sm:pl-7 max-w-full sm:max-w-md space-y-3">
      <div className="flex items-center justify-between gap-2">
        {/* Never green here: a tick before anything is saved is what let a candidate
            leave believing a bank step that saved nothing was done. */}
        <div className="text-xs font-bold text-gray-800">Check your bank details</div>
        {onView && (
          <button
            type="button"
            onClick={onView}
            className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-700 hover:text-slate-900 underline underline-offset-2 cursor-pointer shrink-0"
          >
            <EyeIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            View document
          </button>
        )}
      </div>
      <p className="text-[11px] text-slate-500 leading-snug">
        We filled in what we could read. Check each field against your document, fix anything wrong, and type in anything left empty.
      </p>

      {warnings?.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-3.5 space-y-1">
          <div className="flex items-center gap-1.5 text-xs font-bold text-amber-900">
            <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
            Please check this
          </div>
          <ul className="text-[11px] text-amber-900/90 space-y-0.5 list-disc pl-4">
            {warnings.map((w) => <li key={w}>{w}</li>)}
          </ul>
        </div>
      )}

      {/* Account number */}
      <div className="space-y-1">
        <label htmlFor={`${idPrefix}-account`} className="block text-xs font-bold text-gray-700">
          Account number <span className="text-red-500">*</span>
        </label>
        <input
          id={`${idPrefix}-account`}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder={`${MIN_ACCOUNT_DIGITS}-${MAX_ACCOUNT_DIGITS} digits`}
          maxLength={MAX_ACCOUNT_DIGITS + 6}
          value={account}
          aria-invalid={showErrors && !accountValid}
          onChange={(e) => onChange({ account: e.target.value.replace(/[^\d\s-]/g, '') })}
          onBlur={commit}
          disabled={disabled}
          className={`${inputClass(showErrors && !accountValid, confirmed && accountValid)} font-mono`}
        />
        {accountDiffers && (
          <p className="text-[11px] font-bold text-amber-600 leading-snug">
            This is different from what we read on your document. If you are sure it is right, keep it — your recruiter will check it.
          </p>
        )}
        {showErrors && !accountValid ? (
          <p className="text-[11px] font-bold text-red-500">
            Enter your account number ({MIN_ACCOUNT_DIGITS}-{MAX_ACCOUNT_DIGITS} digits).
          </p>
        ) : !account && <p className="text-[11px] text-slate-500">{UNREAD_HINT}</p>}
      </div>

      {/* IFSC */}
      <div className="space-y-1">
        <label htmlFor={`${idPrefix}-ifsc`} className="block text-xs font-bold text-gray-700">
          IFSC code <span className="text-red-500">*</span>
        </label>
        <input
          id={`${idPrefix}-ifsc`}
          type="text"
          autoComplete="off"
          autoCapitalize="characters"
          placeholder="e.g. SBIN0001234"
          maxLength={14}
          value={ifsc}
          aria-invalid={(showErrors && !ifscValid) || ifscLookup?.valid === false}
          onChange={(e) => onChange({ ifsc: normalizeIfscInput(e.target.value) })}
          onBlur={commit}
          disabled={disabled}
          className={`${inputClass((showErrors && !ifscValid) || ifscLookup?.valid === false, confirmed && ifscValid && ifscLookup?.valid)} font-mono uppercase`}
        />
        <div aria-live="polite" className="text-[11px] leading-snug">
          {lookupState === 'loading' && <p className="text-slate-400">Checking…</p>}
          {ifscLookup?.valid && (
            <p className="text-emerald-700 font-semibold">
              {ifscLookup.bankName}{ifscLookup.branch ? ` — ${ifscLookup.branch}` : ''}
            </p>
          )}
          {ifscLookup?.valid === false && (
            <p className="text-red-500 font-bold">This IFSC code is not in the RBI branch list. Please check it.</p>
          )}
          {ifscSuggested && normalizedIfsc === ifscSuggested && (
            <p className="text-amber-600 font-semibold">
              We worked this out from the MICR code on your document. Please check it against your passbook, cheque or bank app.
            </p>
          )}
          {showErrors && !ifscValid ? (
            <p className="text-red-500 font-bold">An IFSC code is 11 characters: 4 letters, a zero, then 6 letters or numbers.</p>
          ) : !ifsc && <p className="text-slate-500">{UNREAD_HINT}</p>}
        </div>
      </div>

      {/* Holder name */}
      <div className="space-y-1">
        <label htmlFor={`${idPrefix}-holder`} className="block text-xs font-bold text-gray-700">
          Name on the account <span className="text-red-500">*</span>
        </label>
        <input
          id={`${idPrefix}-holder`}
          type="text"
          autoComplete="off"
          placeholder="As printed by your bank"
          maxLength={100}
          value={holderName}
          aria-invalid={showErrors && !holderValid}
          onChange={(e) => onChange({ holderName: e.target.value.replace(/[^A-Za-z.' -]/g, '') })}
          onBlur={commit}
          disabled={disabled}
          className={inputClass(showErrors && !holderValid, confirmed && holderValid)}
        />
        {showErrors && !holderValid ? (
          <p className="text-[11px] font-bold text-red-500">Enter the name on your account, in letters only, as your bank prints it.</p>
        ) : !holderName && <p className="text-[11px] text-slate-500">{UNREAD_HINT}</p>}
      </div>

      <div className="flex items-start gap-2 pt-1">
        <div className="flex items-center h-5">
          <input
            id={`${idPrefix}-confirm`}
            type="checkbox"
            checked={!!confirmed}
            onChange={(e) => onConfirmChange(e.target.checked)}
            disabled={disabled}
            className="w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
          />
        </div>
        <label htmlFor={`${idPrefix}-confirm`} className="text-[11px] text-gray-700 leading-snug cursor-pointer select-none font-medium">
          I confirm I have checked my account number, IFSC code and account name against my document. I understand that incorrect details will result in failed salary transfers.
        </label>
      </div>
      {hasAttemptedSubmit && !confirmed && (
        <p className="text-[11px] font-bold text-red-500">Please check the box to confirm your bank details.</p>
      )}
      {confirmed && !complete && (
        <p role="alert" className="text-[11px] font-bold text-red-500">
          Not saved yet. Fix the field marked in red and your details will save.
        </p>
      )}
    </div>
  );
}
